import type { ActionContext } from 'js/lib/ActionContext';

import type {
  Action,
  ActionType,
  OptionProps,
  SessionActionMapper,
  SimulatorSession,
} from 'bundles/widget-simulator/types';
import * as WidgetSimulatorSessionUtils from 'bundles/widget-simulator/utils/WidgetSimulatorSessionUtils';
import uuid from 'bundles/widget-simulator/utils/uuid';

export const setManifest = (actionContext: ActionContext, { manifest }: $TSFixMe) => {
  actionContext.dispatch('SIMULATOR_SET_MANIFEST', { manifest });
};

export const resetApp = (actionContext: ActionContext) => {
  actionContext.dispatch('SIMULATOR_RESET_APP');
};

export const clearLog = (actionContext: ActionContext) => {
  actionContext.dispatch('SIMULATOR_CLEAR_LOG');
};

export const reloadFrame = (actionContext: ActionContext) => {
  actionContext.dispatch('SIMULATOR_RELOAD_FRAME');
};

export const setFrameUnmounted = (actionContext: ActionContext) => {
  actionContext.dispatch('SIMULATOR_FRAME_UNMOUNTED');
};

let isFirstLoad = true;
const makeSessionAction = <Payload>(fn: SessionActionMapper<Payload>) => {
  return async (actionContext: ActionContext, payload: Payload): Promise<void> => {
    return fn(payload, actionContext).then((session) => {
      actionContext.dispatch('SIMULATOR_SET_SESSION', { session });

      if (isFirstLoad) {
        isFirstLoad = false;
      } else {
        resetApp(actionContext);
      }
    });
  };
};

export const loadSimulatorSession = makeSessionAction((): Promise<SimulatorSession> => {
  return WidgetSimulatorSessionUtils.loadSimulatorSession();
});

export const setProtocol = makeSessionAction<{ protocol: string | undefined }>(
  ({ protocol }, actionContext: ActionContext) => {
    clearLog(actionContext);
    return WidgetSimulatorSessionUtils.setProtocol(protocol);
  }
);

export const setUrl = makeSessionAction<{ url: string | undefined }>(({ url }, actionContext: ActionContext) => {
  clearLog(actionContext);
  return WidgetSimulatorSessionUtils.setUrl(url);
});

export const setSelectedCourseScope = makeSessionAction<{ selectedIndex: number }>(({ selectedIndex }) => {
  return WidgetSimulatorSessionUtils.setSelectedCourseScope(selectedIndex);
});

export const addCourseScope = makeSessionAction<{ label: string }>(({ label }) => {
  return WidgetSimulatorSessionUtils.addCourseScope({ label });
});

export const updateCourseScope = makeSessionAction<{ id: string; newProps: OptionProps }>(({ id, newProps }) => {
  return WidgetSimulatorSessionUtils.updateCourseScope(id, newProps);
});

export const removeCourseScope = makeSessionAction<{ index: number }>(({ index }) => {
  return WidgetSimulatorSessionUtils.removeCourseScope(index);
});

export const setSelectedPluginScope = makeSessionAction<{ selectedIndex: number }>(({ selectedIndex }) => {
  return WidgetSimulatorSessionUtils.setSelectedPluginScope(selectedIndex);
});

export const addPluginScope = makeSessionAction<OptionProps>(({ label }) => {
  return WidgetSimulatorSessionUtils.addPluginScope({ label });
});

export const updatePluginScope = makeSessionAction<{ id: string; newProps: OptionProps }>(({ id, newProps }) => {
  return WidgetSimulatorSessionUtils.updatePluginScope(id, newProps);
});

export const removePluginScope = makeSessionAction<{ index: number }>(({ index }) => {
  return WidgetSimulatorSessionUtils.removePluginScope(index);
});

export const setSelectedSessionConfig = makeSessionAction<{ selectedIndex: number }>(({ selectedIndex }) => {
  return WidgetSimulatorSessionUtils.setSelectedSessionConfig(selectedIndex);
});

export const addSessionConfig = makeSessionAction<OptionProps>(({ label, config }) => {
  return WidgetSimulatorSessionUtils.addSessionConfig({ label, config });
});

export const updateSessionConfig = makeSessionAction<{ id: string; newProps: OptionProps }>(({ id, newProps }) => {
  return WidgetSimulatorSessionUtils.updateSessionConfig(id, newProps);
});

export const removeSessionConfig = makeSessionAction<{ index: number }>(({ index }) => {
  return WidgetSimulatorSessionUtils.removeSessionConfig(index);
});

export const setSelectedGraderConfig = makeSessionAction<{ selectedIndex: number }>(({ selectedIndex }) => {
  return WidgetSimulatorSessionUtils.setSelectedGraderConfig(selectedIndex);
});

export const addGraderConfig = makeSessionAction<OptionProps>(({ label, config }) => {
  return WidgetSimulatorSessionUtils.addGraderConfig({ label, config });
});

export const updateGraderConfig = makeSessionAction<{ id: string; newProps: OptionProps }>(({ id, newProps }) => {
  return WidgetSimulatorSessionUtils.updateGraderConfig(id, newProps);
});

export const removeGraderConfig = makeSessionAction<{ index: number }>(({ index }) => {
  return WidgetSimulatorSessionUtils.removeGraderConfig(index);
});

export const setAnswer = (actionContext: ActionContext, { answer }: $TSFixMe): void => {
  actionContext.dispatch('SIMULATOR_SET_ANSWER', { answer });
};

export const submitForGrading = (actionContext: ActionContext) => {
  actionContext.dispatch('SIMULATOR_SUBMIT_FOR_GRADING');

  const WidgetSimulatorStore = actionContext.getStore('WidgetSimulatorStore');
  const url = WidgetSimulatorStore.getFullUrl();
  const graderConfig = WidgetSimulatorStore.getSelectedGraderConfig();
  const sessionConfig = WidgetSimulatorStore.getSelectedSessionConfig();
  const answer = WidgetSimulatorStore.getAnswer();

  if (!graderConfig) {
    // TODO: Create a modal for this.
    // eslint-disable-next-line no-alert
    return alert('Please select a grader configuration');
  }

  if (!sessionConfig) {
    // TODO: Create a modal for this.
    // eslint-disable-next-line no-alert
    return alert('Please select a session configuration');
  }

  fetch(`${url}/grader.js`)
    .then((response) => {
      if (response.status < 200 || response.status >= 400) {
        throw new Error('Unable to load grader.js');
      }

      return response.text();
    })
    .then((graderScript) => {
      return WidgetSimulatorSessionUtils.submitForGrading(
        graderScript,
        graderConfig.config,
        sessionConfig.config,
        answer.answer
      );
    })
    .then(({ success: evaluation, failure }) => {
      if (failure) {
        throw new Error(`Grading failed: ${failure.message}`);
      }

      actionContext.dispatch('SIMULATOR_SET_EVALUATION', { evaluation });
      reloadFrame(actionContext);
    })
    .catch((error) => {
      // TODO: Create a modal for this.
      // eslint-disable-next-line no-alert
      alert(error.toString());
    });

  return undefined;
};

export const clearAnswer = (actionContext: ActionContext) => {
  actionContext.dispatch('SIMULATOR_CLEAR_ANSWER');
  clearLog(actionContext);
  reloadFrame(actionContext);
};

export const toggleServerSideGrading = makeSessionAction(() => {
  return WidgetSimulatorSessionUtils.toggleServerSideGrading();
});

// TODO: Migrate to take an { action: ActionDetails } argument instead.
export const logAction = (
  actionContext: ActionContext,
  {
    name,
    type,
    description,
    request,
    response,
    errors,
    onComplete,
  }: {
    name: string;
    type: ActionType;
    description: string;
    // TODO: Type me!
    request: any;
    response: any;
    errors: any;
    onComplete: any;
  }
) => {
  const id = uuid();
  const time = Date.now();
  const action: Action = { id, time, name, type, description, request, response, errors };
  actionContext.dispatch('SIMULATOR_LOG_ACTION', action);

  if (onComplete) {
    onComplete(id);
  }
};

// TODO: Type me
export const updateLoggedAction = (actionContext: ActionContext, { id, newProps }: { id: string; newProps: any }) => {
  actionContext.dispatch('SIMULATOR_UPDATE_LOGGED_ACTION', { id, newProps });
};

// TODO: Type me
export const addLoggedActionErrors = (actionContext: ActionContext, { id, errors }: { id: string; errors: any }) => {
  actionContext.dispatch('SIMULATOR_ADD_LOGGED_ACTION_ERRORS', { id, errors });
};
